/**
 * GA4 events for the public scope.
 *
 * ONE DELEGATED LISTENER, NOT A CALL PER COMPONENT. Every measurable action on
 * the public site is a click on something, so the components declare what they
 * are with `data-ga` attributes and this file turns those into gtag calls. The
 * alternative - a `gtag(...)` line inside each Blade and each Alpine component -
 * is how an analytics layer becomes twenty half-maintained call sites that
 * nobody can enumerate, and it puts a vendor's function name in files that have
 * nothing to do with the vendor.
 *
 * CONSENT IS THE GATE, AND IT IS NOT A QUEUE. `window.HubGA.load()` only runs
 * once analytics cookies are accepted (see partials/_google-analytics). Until
 * then `window.gtag` does not exist and every event here is DROPPED, not held:
 * flushing a backlog the moment somebody presses Accept would report activity
 * that happened while they had not agreed to be measured, which is the thing
 * the banner exists to prevent.
 *
 * NO PII, EVER. The only free text that leaves is the site-search term, which is
 * GA4's own `search_term` dimension and the entire point of measuring search; it
 * is trimmed and capped. Nothing here reads an email, a name, a purchase code, a
 * licence key or a URL that carries one.
 */

/** Truncate anything free-text so a pasted paragraph cannot ride along. */
const cap = (value, max = 100) => String(value ?? '').trim().slice(0, max);

/**
 * Send one event, or do nothing at all.
 *
 * The guard is `window.gtag` rather than a flag of our own: gtag is defined by
 * `HubGA.load()` and by nothing else, so "is it safe to send" and "has the
 * visitor consented" are the same question with one answer.
 */
export function track(name, params = {}) {
    if (typeof window.gtag !== 'function') return;

    const clean = {};
    for (const [key, value] of Object.entries(params)) {
        if (value === null || value === undefined || value === '') continue;
        clean[key] = typeof value === 'string' ? cap(value) : value;
    }

    window.gtag('event', name, clean);
}

/**
 * The scope the visitor is in, sent with every event.
 *
 * `is_envato` is the referral flag (App\Http\Middleware\DetectEnvatoReferral),
 * shared into the layout as a data attribute. It is the one piece of context
 * that changes what a number MEANS: a marketplace visitor arrives already
 * owning a product, so their docs traffic is support and ours is evaluation.
 */
function scope() {
    const root = document.documentElement;

    return {
        is_envato: root.dataset.envato === '1' ? 1 : 0,
        color_scheme: root.getAttribute('data-theme') || 'system',
    };
}

/**
 * Declarative click tracking.
 *
 *   <a data-ga="docs_vote" data-ga-label="up" data-ga-item="verba">
 *
 * `data-ga` is the event name; every other `data-ga-*` becomes a parameter with
 * the prefix stripped. `closest` rather than a direct match, so a click landing
 * on the glyph inside a button still reports the button.
 */
function bindClicks() {
    document.addEventListener('click', (event) => {
        const el = event.target.closest?.('[data-ga]');
        if (!el) return;

        const params = { ...scope() };
        for (const [key, value] of Object.entries(el.dataset)) {
            if (key === 'ga' || !key.startsWith('ga')) continue;
            // `gaItemName` -> `item_name`
            const name = key
                .slice(2)
                .replace(/^[A-Z]/, (c) => c.toLowerCase())
                .replace(/[A-Z]/g, (c) => '_' + c.toLowerCase());
            params[name] = value;
        }

        track(el.dataset.ga, params);
    }, { passive: true });
}

/**
 * Outbound links, without an attribute on every one of them.
 *
 * GA4 measures outbound clicks itself under Enhanced Measurement - but that is a
 * property setting somebody has to have left on, and it reports the URL and
 * nothing else. This adds the one thing we actually want to segment by: WHICH
 * surface the visitor was on when they left for the marketing site, because
 * those are the conversion exits.
 */
function bindOutbound() {
    document.addEventListener('click', (event) => {
        const link = event.target.closest?.('a[href^="http"]');
        if (!link || link.hasAttribute('data-ga')) return;

        let host;
        try {
            host = new URL(link.href).host;
        } catch {
            return;
        }
        if (host === window.location.host) return;

        track('outbound_click', {
            ...scope(),
            link_domain: host,
            link_url: cap(link.href, 200),
            from_surface: document.body.dataset.gaSurface || 'unknown',
        });
    }, { passive: true });
}

/**
 * Site search.
 *
 * Two events, because they answer different questions: `search` says people are
 * looking (and for what), `select_search_result` says the index answered. A
 * search with no selection is the one that tells us the corpus has a hole in it.
 */
function bindSearch() {
    window.addEventListener('hub:search', (event) => {
        const detail = event.detail || {};
        track('search', {
            ...scope(),
            search_term: cap(detail.term, 100),
            results: Number(detail.results) || 0,
        });
    });

    window.addEventListener('hub:search-select', (event) => {
        const detail = event.detail || {};
        track('select_search_result', {
            ...scope(),
            search_term: cap(detail.term, 100),
            result_group: cap(detail.group, 40),
            result_position: Number(detail.position) || 0,
        });
    });
}

/**
 * Reading depth on a documentation or KB article.
 *
 * NOT a scroll-percentage ladder. 25/50/75/100 on a page whose length varies by
 * a factor of ten measures the page, not the reader. This fires once, when the
 * END of the article body has been on screen - which is the only depth signal
 * that means the same thing on a 300-word answer and a 4000-word guide.
 */
function bindReadDepth() {
    const article = document.querySelector('[data-ga-article]');
    if (!article || typeof IntersectionObserver === 'undefined') return;

    const end = article.querySelector('[data-ga-article-end]') || article.lastElementChild;
    if (!end) return;

    const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            track('article_read', {
                ...scope(),
                article: cap(article.dataset.gaArticle, 120),
                article_type: cap(article.dataset.gaArticleType || 'docs', 20),
                item: cap(article.dataset.gaItem, 60),
            });
            observer.disconnect();
            return;
        }
    }, { rootMargin: '0px 0px -20% 0px' });

    observer.observe(end);
}

/**
 * The page's own context, sent once as a `page_view` parameter set.
 *
 * GA4 sends its own `page_view` on config; this adds the dimensions that make
 * the public scope legible - which product a page belongs to, and whether the
 * visitor arrived from a marketplace.
 */
function bindPageContext() {
    const surface = document.body.dataset.gaSurface;
    if (!surface) return;

    track('hub_page_view', {
        ...scope(),
        surface: cap(surface, 40),
        item: cap(document.body.dataset.gaItem, 60),
    });
}

export default function initAnalytics() {
    bindClicks();
    bindOutbound();
    bindSearch();
    bindReadDepth();

    // AFTER CONSENT, TOO. A visitor who accepts on the page they landed on has
    // already passed this point, so the banner re-fires the context event rather
    // than that first page going unrecorded.
    bindPageContext();
    window.addEventListener('hub:analytics-ready', bindPageContext);
}
